import React, { useMemo, useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Box, ButtonBase, InputAdornment, Paper, Popper, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealBase } from "store/types";

function getLabel(value: DealBase[]): string {
  if (value.length === 0) return "All Deals";
  if (value.length === 1) return value[0].title;
  return "Multiple";
}

function dealsChanged(a: DealBase[], b: DealBase[]): boolean {
  if (a.length !== b.length) return true;
  const aIds = new Set(a.map((d) => d.id));
  return b.some((d) => !aIds.has(d.id));
}

interface Props {
  deals: DealBase[];
  selectedDeals: DealBase[];
  setSelectedDeals: (deals: DealBase[]) => void;
}

export const DealFilter: React.FC<Props> = ({ deals, selectedDeals, setSelectedDeals }) => {
  const [open, setOpen] = useState(false);
  const [localValue, setLocalValue] = useState<DealBase[]>([]);
  const [search, setSearch] = useState("");
  const anchorRef = useRef<HTMLButtonElement>(null);
  const valueAtOpenRef = useRef<DealBase[]>([]);

  const filtered = useMemo(() => {
    if (!search.trim()) return deals;
    const q = search.toLowerCase();
    return deals.filter((d) => d.title.toLowerCase().includes(q));
  }, [deals, search]);

  const handleOpen = () => {
    setLocalValue(selectedDeals);
    valueAtOpenRef.current = selectedDeals;
    setOpen(true);
  };

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      setSearch("");
    } else {
      handleOpen();
    }
  };

  const handleClickOutside = () => {
    setOpen(false);
    setSearch("");
    if (dealsChanged(valueAtOpenRef.current, localValue)) {
      setSelectedDeals(localValue);
    }
  };

  const handleToggleDeal = (deal: DealBase) => {
    const isSelected = localValue.some((d) => d.id === deal.id);
    setLocalValue(isSelected ? localValue.filter((d) => d.id !== deal.id) : [...localValue, deal]);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue([]);
    setSelectedDeals([]);
  };

  const displayValue = open ? localValue : selectedDeals;

  const tooltipTitle =
    displayValue.length >= 1 ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {displayValue.map((d) => (
          <Typography key={d.id} sx={{ fontSize: 11, lineHeight: "16px" }}>
            {d.title}
          </Typography>
        ))}
      </Box>
    ) : (
      ""
    );

  return (
    <Box sx={{ display: "inline-flex", position: "relative" }}>
      {open && <Box sx={{ position: "fixed", inset: 0, zIndex: 1299 }} onClick={handleClickOutside} />}

      <MaterialTooltip title={tooltipTitle} placement="bottom" arrow isShow={displayValue.length >= 1 && !open}>
        <ButtonBase
          ref={anchorRef}
          onClick={handleToggle}
          disableRipple
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "120px",
            px: "8px",
            py: "4px",
            backgroundColor: "white",
            border: `1px solid ${open ? "#496682" : "#7b92a9"}`,
            borderRadius: "32px",
            transition: "border-color 0.2s",
            "&:hover": { borderColor: "#496682" }
          }}
        >
          <Typography
            noWrap
            sx={{ fontSize: 12, fontWeight: 400, color: "#071f35", lineHeight: "16px", maxWidth: "70px" }}
          >
            {getLabel(displayValue)}
          </Typography>
          {selectedDeals.length >= 1 && !open && (
            <Box
              component="span"
              onClick={handleClearAll}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
                color: "#496682",
                "&:hover": { color: "#071f35" }
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </Box>
          )}
          <ArrowDropDownIcon
            sx={{
              fontSize: 16,
              color: "#071f35",
              flexShrink: 0,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)"
            }}
          />
        </ButtonBase>
      </MaterialTooltip>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <Paper
          elevation={3}
          sx={{ mt: "4px", borderRadius: "8px", border: "1px solid #dbe8f5", width: 200, overflow: "hidden" }}
        >
          <Box sx={{ p: "8px", borderBottom: "1px solid #dbe8f5" }}>
            <TextField
              autoFocus
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: "#7b92a9" }} />
                  </InputAdornment>
                )
              }}
              sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 12 } }}
            />
          </Box>

          <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
            <Box
              onClick={() => setLocalValue([])}
              sx={{
                px: "12px",
                py: "8px",
                cursor: "pointer",
                backgroundColor: localValue.length === 0 ? "#ecf1f8" : "transparent",
                "&:hover": { backgroundColor: "#ecf1f8" }
              }}
            >
              <Typography sx={{ fontSize: 12, color: "#071f35" }}>All Deals</Typography>
            </Box>

            {filtered.map((deal) => {
              const isSelected = localValue.some((d) => d.id === deal.id);
              return (
                <Box
                  key={deal.id}
                  onClick={() => handleToggleDeal(deal)}
                  sx={{
                    px: "12px",
                    py: "8px",
                    cursor: "pointer",
                    backgroundColor: isSelected ? "#ecf1f8" : "transparent",
                    "&:hover": { backgroundColor: "#ecf1f8" }
                  }}
                >
                  <Typography noWrap sx={{ fontSize: 12, color: "#071f35" }}>
                    {deal.title}
                  </Typography>
                </Box>
              );
            })}

            {filtered.length === 0 && (
              <Box sx={{ px: "12px", py: "8px" }}>
                <Typography sx={{ fontSize: 12, color: "#7b92a9" }}>No deals found</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};
